import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import peopleService from '../services/peopleService';
import {Person} from '../types/Person';
import {DataTable, DataTableFilterMeta} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {useNavigate} from "react-router-dom";
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton} from "../../../layout/admin";
import {FilterMatchMode} from "primereact/api";
import {handleError} from "../../../common/utils/errorHandling";
import {useToast} from "../../../common/contexts/ToastContext";
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";

const PeopleList: React.FC = () => {
  const {t} = useTranslation();
  const {showError, showSuccess} = useToast();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [people, setPeople] = useState<Person[]>([]);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    name: {value: null, matchMode: FilterMatchMode.CONTAINS},
    surname: {value: null, matchMode: FilterMatchMode.CONTAINS}
  });

  useEffect(() => {
    fetchPeople();
  }, []);

  const fetchPeople = async (): Promise<void> => {
    const peopleData = await peopleService.getAllPeople();
    setPeople(peopleData);
  };

  const handleDelete = async (person: Person): Promise<void> => {
    showConfirmDialog({
      message: t('admin.common.messages.confirmDeleteWithName', {name: `${person.name} ${person.surname}`}),
      accept: async () => {
        await handleError(
          async () => {
            await peopleService.deletePerson(person.id);
            await fetchPeople();
          },
          t('admin.people.errors.deleting'),
          undefined,
          showError
        );
      }
    });
  };

  const handleEdit = (person: Person) => {
    navigate(`${person.id}/edit`);
  };

  const handleCreateNew = () => {
    navigate('new');
  };

  return (
    <>
      <AdminFormHeader
        title={t('admin.people.title.list')}
        subtitle={t('admin.people.subtitle.list')}
      />

      <AdminButton
        onClick={handleCreateNew}
        variant="black"
        label={t('admin.people.actions.createNew')}
      />

      <DataTable
        value={people}
        stripedRows
        paginator
        rows={10}
        filterDisplay="row"
        filters={filters}
        columnResizeMode="fit"
        resizableColumns
        showGridlines
      >
        <Column
          sortField="name"
          header={t('admin.common.columns.name')}
          sortable
          filter
          filterField="name"
          filterPlaceholder={t('admin.common.filters.searchByName')}
          showFilterMenu={false}
          showClearButton={false}
          body={(person: Person) => (
            <strong>{person.name}</strong>
          )}/>
        <Column
          sortField="surname"
          header={t('admin.people.columns.surname')}
          sortable
          filter
          filterField="surname"
          filterPlaceholder={t('admin.people.filters.searchBySurname')}
          showFilterMenu={false}
          showClearButton={false}
          body={(person: Person) => (
            <span>{person.surname}</span>
          )}/>
        <Column header={t('admin.common.columns.actions')} body={(person: Person) => (
          <div>
            <Button
              onClick={() => handleEdit(person)}
              icon="fa fa-edit"
              severity="info"
              size="small"
              outlined
              className="mr-2"
              aria-label={t('admin.common.actions.edit')}
              tooltip={t('admin.common.actions.edit')}
              tooltipOptions={{position: 'top'}}
            />
            <Button
              onClick={() => handleDelete(person)}
              icon="fa fa-trash"
              severity="danger"
              size="small"
              outlined
              aria-label={t('admin.common.actions.delete')}
              tooltip={t('admin.common.actions.delete')}
              tooltipOptions={{position: 'top'}}
            />
          </div>)}/>
      </DataTable>
    </>
  );
};

export default PeopleList;

